import { __assign, __rest } from "tslib";
import React from 'react';
import { BLOCK_TYPES, depthClassName, getTextDirection, getDirectionFromAlignmentAndTextDirection, } from 'wix-rich-content-common';
import { getBlockIndex } from './draftUtils';
import redraft from 'wix-redraft';
import classNames from 'classnames';
import endsWith from 'lodash/endsWith';
import List from '../List';
import { isPaywallSeo, getPaywallSeoClass } from './paywallSeo';
import getPluginViewers from '../getPluginViewers';
import { kebabToCamelObjectKeys } from './textUtils';
// import { staticInlineStyleMapper } from '../staticInlineStyleMapper';
import { combineMappers } from './combineMappers';
import { getInteractionWrapper, DefaultInteractionWrapper, } from './getInteractionWrapper';
import Anchor from '../components/Anchor';
import customStyleViewer from '../../styleRenderer';
var isEmptyContentState = function (raw) {
    return !raw ||
        !raw.blocks ||
        (raw.blocks.length === 1 &&
            raw.blocks[0].text === '' &&
            raw.blocks[0].type === 'unstyled');
};
var isEmptyBlock = function (_a) {
    var _ = _a[0], data = _a[1];
    return data && data.length === 0;
}; // eslint-disable-line no-unused-vars
var getBlockDepth = function (contentState, key) {
    return contentState.blocks.find(function (block) { return block.key === key; }).depth || 0;
};
var getBlockStyleClasses = function (data, mergedStyles, textDirection, classes, isListItem) {
    var rtl = getDirectionFromAlignmentAndTextDirection(data.textAlignment, textDirection || data.textDirection) === 'rtl';
    var defaultTextAlignment = rtl ? 'right' : 'left';
    var languageDirection = textDirection || data.textDirection || 'ltr';
    var alignmentClass = data.textAlignment || defaultTextAlignment;
    var directionRTL = isListItem ? rtl : languageDirection !== 'ltr';
    var directionClass = directionRTL ? mergedStyles.rtl : mergedStyles.ltr;
    return classNames(classes, directionClass, mergedStyles[alignmentClass]);
};
var blockCount = 0;
var blockDataToStyle = function (_a) {
    var dynamicStyles = _a.dynamicStyles;
    return kebabToCamelObjectKeys(dynamicStyles);
};
/* const getInline = (inlineStyleMappers, mergedStyles) =>
  combineMappers(
    [...inlineStyleMappers, staticInlineStyleMapper],
    mergedStyles
  );*/
var getBlocks = function (mergedStyles, textDirection, context, addAnchorsPrefix) {
    var getList = function (ordered) { return function (items, blockProps) {
        var fixedItems = items.map(function (item) { return (item.length ? item : [' ']); });
        var props = {
            key: blockProps.keys[0],
            items: fixedItems,
            ordered: ordered,
            mergedStyles: mergedStyles,
            textDirection: textDirection,
            blockProps: blockProps,
            getBlockStyleClasses: getBlockStyleClasses,
            blockDataToStyle: blockDataToStyle,
            getBlockDepth: getBlockDepth,
            context: context,
        };
        return React.createElement(List, __assign({}, props));
    }; };
    var blockFactory = function (type, style) {
        return function (children, blockProps) {
            return children.map(function (child, i) {
                var _a, _b;
                var depth = getBlockDepth(context.contentState, blockProps.keys[i]);
                var direction = getDirectionFromAlignmentAndTextDirection((_a = blockProps.data[0]) === null || _a === void 0 ? void 0 : _a.textAlignment, (_b = blockProps.data[0]) === null || _b === void 0 ? void 0 : _b.textDirection);
                var directionClassName = "public-DraftStyleDefault-text-" + direction;
                var ChildTag = typeof type === 'string' ? type : type(child);
                var blockIndex = getBlockIndex(context.contentState, blockProps.keys[i]);
                var interactions = blockProps.data[i].interactions;
                var BlockWrapper = Array.isArray(interactions)
                    ? getInteractionWrapper({ interactions: interactions, context: context })
                    : DefaultInteractionWrapper;
                var _child = isEmptyBlock(child) ? React.createElement("br", null) : child;
                var inner = (React.createElement(ChildTag, { id: "viewer-" + blockProps.keys[i], className: classNames(getBlockStyleClasses(blockProps.data[i], mergedStyles, textDirection, mergedStyles[style]), depthClassName(depth), directionClassName, isPaywallSeo(context.seoMode) &&
                        getPaywallSeoClass(context.seoMode.paywall, blockIndex)), style: blockDataToStyle(blockProps.data[i]), key: blockProps.keys[i] }, _child));
                var blockWrapper = (React.createElement(BlockWrapper, { key: blockProps.keys[i] + "_wrap" }, inner));
                var shouldAddAnchors = addAnchorsPrefix && !isEmptyBlock(child);
                var resultBlock = blockWrapper;
                if (shouldAddAnchors) {
                    blockCount++;
                    var anchorKey = "" + addAnchorsPrefix + blockCount;
                    resultBlock = (React.createElement(React.Fragment, { key: blockProps.keys[i] + "_wrap" },
                        blockWrapper,
                        React.createElement(Anchor, { type: typeof type === 'string' ? type : 'paragraph', key: anchorKey, anchorKey: anchorKey })));
                }
                return resultBlock;
            });
        };
    };
    return {
        unstyled: blockFactory(function (child) { return (isEmptyBlock(child) ? 'div' : 'p'); }, 'text'),
        blockquote: blockFactory('blockquote', 'quote'),
        'header-one': blockFactory('h1', 'headerOne'),
        'header-two': blockFactory('h2', 'headerTwo'),
        'header-three': blockFactory('h3', 'headerThree'),
        'header-four': blockFactory('h4', 'headerFour'),
        'header-five': blockFactory('h5', 'headerFive'),
        'header-six': blockFactory('h6', 'headerSix'),
        'code-block': blockFactory('pre', 'codeBlock'),
        'unordered-list-item': getList(false),
        'ordered-list-item': getList(true),
    };
};
var getEntities = function (typeMappers, context, styles, addAnchorsPrefix) {
    var emojiViewerFn = function (emojiUnicode, data, _a) {
        var key = _a.key;
        return (React.createElement("span", { key: key, style: { fontFamily: 'cursive' } }, emojiUnicode));
    };
    return __assign({ EMOJI_TYPE: emojiViewerFn }, getPluginViewers(typeMappers, context, styles, function (type) {
        if (addAnchorsPrefix) {
            blockCount++;
            var anchorKey = "" + addAnchorsPrefix + blockCount;
            return React.createElement(Anchor, { type: type, key: anchorKey, anchorKey: anchorKey });
        }
        else {
            return null;
        }
    }));
};
var normalizeContentState = function (contentState) { return (__assign(__assign({}, contentState), { blocks: contentState.blocks.map(function (block) {
        if (block.type === 'atomic') {
            return block;
        }
        var data = __assign({}, block.data);
        var direction = getTextDirection(block.text);
        if (direction === 'rtl') {
            data.textDirection = direction;
        }
        var text = block.text;
        if (endsWith(text, '\n')) {
            text += '\n';
        }
        // DA edit
        // hacky parent block color handling, this takes
        // the first color of the block and sets it as the
        // color for the block.
        if (block.inlineStyleRanges.length > 0) {
            var firstColor = block.inlineStyleRanges[0];
            if (firstColor.style &&
                firstColor.style.startsWith('TEXT_TOOLS_COLOR_')) {
                data.dynamicStyles = __assign({ color: firstColor.style.replace('TEXT_TOOLS_COLOR_', '') }, data.dynamicStyles);
            }
        }
        return __assign(__assign({}, block), { depth: 0, data: data,
            text: text });
    }) })); };
var redraftOptions = {
    cleanup: {
        after: BLOCK_TYPES.filter(function (t) { return t.indexOf('header') === -1; }),
        split: true,
        except: [
            'unordered-list-item',
            'ordered-list-item',
            'unstyled',
            'header-one',
            'header-two',
            'header-three',
            'header-four',
            'header-five',
            'header-six',
        ],
    },
    convertFromRaw: function (contentState) { return contentState; },
};
// DA ADDED
/**
 * Styles renderer - render ALL inline styles
 */
var getStyles = function () {
    return function (children, styleArray, params) {
        return customStyleViewer(children, styleArray, params);
    };
};
// END DA ADDED
var convertToReact = function (mergedStyles, textDirection, typeMappers, context, decorators, inlineStyleMappers, initSpoilers, options) {
    if (options === void 0) { options = {}; }
    if (isEmptyContentState(context.contentState)) {
        return null;
    }
    var addAnchors = options.addAnchors, restOptions = __rest(options, ["addAnchors"]);
    var newContentState = initSpoilers
        ? initSpoilers(normalizeContentState(context.contentState))
        : normalizeContentState(context.contentState);
    var addAnchorsPrefix = addAnchors && (addAnchors === true ? 'rcv-block' : addAnchors);
    blockCount = 0;
    return redraft(newContentState, {
        // This is where we differ from RCE, they use inline we use style
        // inline: getInline(inlineStyleMappers, mergedStyles),
        styles: getStyles(),
        blocks: getBlocks(mergedStyles, textDirection, context, addAnchorsPrefix),
        entities: getEntities(combineMappers(typeMappers), context, mergedStyles, addAnchorsPrefix),
        decorators: decorators,
    }, __assign(__assign({}, redraftOptions), restOptions));
};
export { convertToReact };
//# sourceMappingURL=convertContentState.js.map